import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { RxIntuitiveTeethHelperService } from './services/rx-intuitive-teeth-helper.service';
import { IntuitiveIndication, IntuitiveToothForDiagram, IntuitiveToothForTreatment, DiagramToothAndBridgeListViewItem } from './models';
import { RxIntuitiveTeethDiagramMutationService } from './services/teeth-diagram/rx-intuitive-teeth-diagram-mutation.service';
import { RxIntuitiveTeethDiagramViewService } from './services/teeth-diagram/rx-intuitive-teeth-diagram-view.service';
import { IRxIntuitiveTeethOrchestratorFacade } from './models/rx-intuitive-teeth-orchestrator-facade.interface';
import { RxIntuitiveTeethOrchestratorQuery } from './state/rx-intuitive-teeth-orchestrator-query';
import { OrchestratorViewModeType, RxIntuitiveTeethOrchestratorStore } from './state/rx-intuitive-teeth-orchestrator-store';
import { RxIntuitiveTeethDiagramQuery } from './state/rx-intuitive-teeth-diagram-query';
import { ProcedureSectionVisibilityService } from '@shared/services/procedure-section-visibility.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { AppFacade } from 'src/app/app.facade';

@Injectable()
export class RxIntuitiveTeethOrchestratorFacade implements IRxIntuitiveTeethOrchestratorFacade, OnDestroy {
	readonly facadeName: string = 'RxIntuitiveTeethOrchestratorFacade';
	protected readonly facadeDestroy$ = new Subject<void>();

	// ── Pass-through delegates to RxIntuitiveTeethHelperService ──────────────
	// These are single-line delegations that don't warrant extraction into a
	// separate service.
	readonly teethNumberingSystem$ = this.teethHelper.teethNumberingSystem$;
	readonly upperJawBridges$ = this.teethHelper.upperJawBridges$;
	readonly lowerJawBridges$ = this.teethHelper.lowerJawBridges$;
	readonly bridges$ = this.teethHelper.bridges$;
	readonly selectedToothIds$ = this.viewService.selectedToothIds$;
	readonly hasSelectedTeeth$ = this.selectedToothIds$.pipe(map(ids => ids.length > 0));
	readonly isToothEditorShown$: Observable<boolean> = this.teethHelper.isToothEditorShown$;
	readonly previewTreatmentListItems$: Observable<DiagramToothAndBridgeListViewItem[]> = this.teethHelper.previewTreatmentListItems$;

	// ── View service delegates ────────────────────────────────────────────────
	readonly upperJaw$: Observable<IntuitiveToothForDiagram[]> = this.viewService.upperJaw$;
	readonly lowerJaw$: Observable<IntuitiveToothForDiagram[]> = this.viewService.lowerJaw$;
	readonly isTeethDiagramReadOnly$: Observable<boolean> = this.viewService.isTeethDiagramReadOnly$;
	readonly visibleIndications$: Observable<IntuitiveIndication[]> = this.viewService.visibleIndications$;
	readonly selectedTeethIndicationIds$: Observable<IntuitiveIndication[]> = this.viewService.selectedTeethIndicationIds$;
	readonly enabledIndications$: Observable<IntuitiveIndication[]> = this.viewService.enabledIndications$;
	readonly indicationToolbarIds$: Observable<IntuitiveIndication[]> = this.viewService.indicationToolbarIds$;
	readonly fetchRxAndSyncStore$ = this.viewService.fetchRxAndSyncStore$;

	readonly isArtProcedureSelected$: Observable<boolean> = this.sectionVisibilityService.isArtProcedureSelected$;

	/**
	 * Non-default teeth mapped to preview view models.
	 * Kept as a protected-equivalent pass-through — tests access it via cast.
	 */
	protected readonly previewTeeth$: Observable<IntuitiveToothForTreatment[]> = this.teethHelper.previewTeeth$;

	// ── Non-common properties (not shared with bridge facade) ────
	readonly orchestratorViewMode$ = this.orchestratorQuery.viewMode$;
	readonly hasSelectedBridge$ = this.diagramQuery.selectedBridge$.pipe(map(bridge => bridge !== undefined));

	readonly preTreatmentScanValidationError$ = this.appFacade.preTreatmentScanValidationError$;

	// Validation error when pre-treatment enabled but no tooth indication selected (shows when ready to send)
	readonly summaryPreTreatmentValidationError$: Observable<string | null> = combineLatest([
		this.shellQuery.shouldValidateForSend$,
		this.appFacade.isPreTreatmentMissingIndications$
	]).pipe(
		map(([shouldValidate, isMissingIndications]) =>
			shouldValidate && isMissingIndications ? 'PreTreatment.PreTreatmentValidationMessage' : null
		)
	);

	// Combined error for the banner - shows either scan or summary error
	readonly preTreatmentValidationError$: Observable<string | null> = combineLatest([
		this.preTreatmentScanValidationError$,
		this.summaryPreTreatmentValidationError$
	]).pipe(map(([scanError, summaryError]) => scanError ?? summaryError));

	constructor(
		protected readonly teethHelper: RxIntuitiveTeethHelperService,
		protected readonly viewService: RxIntuitiveTeethDiagramViewService,
		protected readonly mutationService: RxIntuitiveTeethDiagramMutationService,
		private readonly orchestratorQuery: RxIntuitiveTeethOrchestratorQuery,
		private readonly orchestratorStore: RxIntuitiveTeethOrchestratorStore,
		private readonly diagramQuery: RxIntuitiveTeethDiagramQuery,
		private readonly sectionVisibilityService: ProcedureSectionVisibilityService,
		private readonly appFacade: AppFacade,
		private readonly shellQuery: ShellQuery
	) {}

	ngOnDestroy(): void {
		this.facadeDestroy$.next();
		this.facadeDestroy$.complete();
	}

	changeViewMode(newViewMode: OrchestratorViewModeType) {
		this.orchestratorStore.changeViewMode(newViewMode);
	}

	// ── Mutation service delegates ────────────────────────────────────────────

	createBridge(selectedToothIds: number[]): void {
		this.mutationService.createBridge(selectedToothIds);
	}

	clearToothSelection(): void {
		this.mutationService.clearToothSelection();
	}

	copyTreatmentDetailsFromSourceTooth(sourceToothId: number): void {
		this.mutationService.copyTreatmentDetailsFromSourceTooth(sourceToothId);
	}

	toggleToothSelection(toothId: number): void {
		this.mutationService.toggleToothSelection(toothId);
	}
}
